/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Star, Quote, MapPin, Calendar, ExternalLink, ArrowRight, Share2, Facebook, Linkedin, Twitter, MessageCircle } from "lucide-react";
import PageHero from "@/components/PageHero";
import { reviewsApi } from "@/lib/api";
import { useSeo, JsonLd } from "@/lib/seo";

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("fr-FR", { month: "long", year: "numeric" }); }
  catch { return ""; }
}

function Stars({ value = 5, size = 14 }) {
  return (
    <span className="inline-flex items-center gap-0.5 text-[#DCB457]">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size} strokeWidth={1.3} fill={n <= value ? "currentColor" : "none"} />
      ))}
    </span>
  );
}

function ShareRow({ review }) {
  const baseUrl = (typeof window !== "undefined" && window.location ? window.location.origin : "https://myanimation.fr");
  const url = `${baseUrl}/avis#review-${review.id}`;
  const text = encodeURIComponent(`${review.title || "Avis client My Animation"} — ${review.author_name}`);
  const encUrl = encodeURIComponent(url);
  const links = [
    { id: "facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}`, label: "Facebook", icon: Facebook },
    { id: "linkedin", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`, label: "LinkedIn", icon: Linkedin },
    { id: "twitter", href: `https://twitter.com/intent/tweet?text=${text}&url=${encUrl}`, label: "Twitter / X", icon: Twitter },
    { id: "whatsapp", href: `https://wa.me/?text=${text}%20${encUrl}`, label: "WhatsApp", icon: MessageCircle },
  ];

  const copy = async (e) => {
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(url);
      // Fallback toast via window.alert is too intrusive — silent on success
    } catch {
      window.prompt("Copiez ce lien :", url);
    }
  };

  return (
    <div className="mt-4 pt-3 border-t border-[#DCB457]/12 flex items-center gap-2" data-testid={`avis-share-${review.id}`}>
      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-white/45">
        <Share2 size={11} strokeWidth={1.4} /> Partager
      </span>
      {links.map((l) => (
        <a
          key={l.id}
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={l.label}
          title={l.label}
          data-testid={`share-${l.id}-${review.id}`}
          className="w-7 h-7 inline-flex items-center justify-center text-white/55 hover:text-[#DCB457] hover:bg-[#DCB457]/8 border border-white/10 hover:border-[#DCB457]/40 transition"
        >
          <l.icon size={12} strokeWidth={1.5} />
        </a>
      ))}
      <button
        type="button"
        onClick={copy}
        aria-label="Copier le lien"
        title="Copier le lien"
        data-testid={`share-copy-${review.id}`}
        className="w-7 h-7 inline-flex items-center justify-center text-white/55 hover:text-[#DCB457] hover:bg-[#DCB457]/8 border border-white/10 hover:border-[#DCB457]/40 transition text-[10px] font-mono"
      >
        ↗
      </button>
    </div>
  );
}

export default function Avis() {
  useSeo({
    title: "Avis clients — Témoignages DJ Mariage | My Animation",
    description: "Retrouvez les avis clients de nos prestations mariage, anniversaire, entreprise et photobooth. Avis publiés sur Google et Mariages.net, complétés par des retours clients directs. My Animation, DJ premium Hauts-de-France.",
    path: "/avis",
    keywords: "avis DJ My Animation, témoignages DJ mariage Lille, avis DJ Hauts-de-France",
  });

  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | Mariage | Anniversaire | Entreprise | Photobooth

  useEffect(() => {
    Promise.all([reviewsApi.publicList(100), reviewsApi.publicStats()])
      .then(([items, s]) => {
        setReviews(items);
        setStats(s);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return reviews;
    return reviews.filter((r) => r.event_type === filter);
  }, [reviews, filter]);

  const eventTypes = useMemo(() => {
    const set = new Set(reviews.map((r) => r.event_type).filter(Boolean));
    return ["all", ...Array.from(set)];
  }, [reviews]);

  return (
    <>
      <PageHero
        eyebrow="Témoignages"
        title={<>L'avis de nos <span className="italic gold-text">clients.</span></>}
        subtitle={
          stats && stats.count > 0
            ? `${stats.average.toFixed(1).replace(".", ",")} / 5 sur ${stats.count} avis clients. Avis publiés sur Google et Mariages.net, complétés par des retours clients directs.`
            : "4,9 / 5 sur 8 avis clients. Avis publiés sur Google et Mariages.net, complétés par des retours clients directs."
        }
        img="https://images.pexels.com/photos/1779487/pexels-photo-1779487.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
      />

      {/* JSON-LD AggregateRating + Reviews → étoiles dans Google */}
      {stats && stats.count > 0 && (
        <JsonLd
          id="jsonld-reviews"
          data={{
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": "https://myanimation.fr/avis",
            name: "My Animation",
            url: "https://myanimation.fr",
            telephone: "+33769224956",
            priceRange: "€€€",
            address: {
              "@type": "PostalAddress",
              addressRegion: "Hauts-de-France",
              addressCountry: "FR",
            },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: String(stats.average),
              reviewCount: String(stats.count),
              bestRating: "5",
              worstRating: "1",
            },
            review: reviews.slice(0, 20).map((r) => ({
              "@type": "Review",
              author: { "@type": "Person", name: r.author_name },
              datePublished: r.published_at || r.created_at,
              reviewRating: {
                "@type": "Rating",
                ratingValue: String(r.rating),
                bestRating: "5",
                worstRating: "1",
              },
              reviewBody: r.content,
            })),
          }}
        />
      )}

      {/* Stats premium — note visible calculée sur les avis en base */}
      <section className="py-20 md:py-24 px-6 md:px-10 lg:px-14 max-w-[1100px] mx-auto">
        <div className="card-luxe p-10 md:p-14 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <div>
            <p className="eyebrow">Note moyenne</p>
            <p className="font-serif-luxe text-6xl gold-text mt-3">
              {stats?.average ? stats.average.toFixed(1).replace(".", ",") : "4,9"}
              <span className="text-3xl text-white/40">/5</span>
            </p>
            <div className="mt-3 inline-block"><Stars value={Math.round(stats?.average || 4.9)} size={18} /></div>
          </div>
          <div className="md:border-x border-[#DCB457]/15">
            <p className="eyebrow">Avis clients</p>
            <p className="font-serif-luxe text-6xl gold-text mt-3">{stats?.count || 8}</p>
            <p className="text-xs text-white/45 mt-2">Google · Mariages.net · retours directs</p>
          </div>
          <div>
            <p className="eyebrow">Notre engagement</p>
            <p className="font-serif-luxe text-4xl mt-3">Transparence <span className="italic gold-text">totale.</span></p>
            <p className="text-xs text-white/45 mt-2">Aucun avis modifié ni trié artificiellement</p>
          </div>
        </div>
        <p className="text-center text-sm text-white/55 mt-6 max-w-2xl mx-auto">
          Avis publiés sur Google et Mariages.net, complétés par des retours clients directs.
        </p>

        {/* External platforms */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <a href="https://www.mariages.net/musique-mariage/my-animation--e304403" target="_blank" rel="noopener noreferrer" data-testid="avis-mariagesnet" className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/15 hover:border-[#DCB457]/45 hover:bg-[#DCB457]/5 transition text-sm text-white/75">
            <ExternalLink size={12} strokeWidth={1.4} /> Voir nos avis sur Mariages.net
          </a>
          <a href="https://www.google.com/search?q=My+Animation+DJ+Lille" target="_blank" rel="noopener noreferrer" data-testid="avis-google" className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/15 hover:border-[#DCB457]/45 hover:bg-[#DCB457]/5 transition text-sm text-white/75">
            <ExternalLink size={12} strokeWidth={1.4} /> Avis Google
          </a>
        </div>
      </section>

      {/* Filtres */}
      {eventTypes.length > 2 && (
        <section className="px-6 md:px-10 lg:px-14 max-w-[1100px] mx-auto">
          <div className="flex flex-wrap justify-center gap-2" data-testid="avis-filters">
            {eventTypes.map((t) => (
              <button
                key={t}
                onClick={() => setFilter(t)}
                data-testid={`avis-filter-${t}`}
                className={`text-[11px] uppercase tracking-[0.18em] px-4 py-2 border transition ${filter === t ? "border-[#DCB457] text-[#DCB457] bg-[#DCB457]/8" : "border-white/12 text-white/55 hover:border-[#DCB457]/40 hover:text-white/80"}`}
              >
                {t === "all" ? "Tous" : t}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="py-16 px-6 md:px-10 lg:px-14 max-w-[1200px] mx-auto">
        {loading ? (
          <p className="text-white/55 italic text-center">Chargement des avis…</p>
        ) : filtered.length === 0 ? (
          <p className="text-white/55 italic text-center">Aucun avis pour ce filtre.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="avis-grid">
            {filtered.map((r) => (
              <div
                key={r.id}
                id={`review-${r.id}`}
                data-testid={`avis-card-${r.id}`}
                className="card-luxe p-6 flex flex-col scroll-mt-24"
              >
                <div className="flex items-start justify-between gap-3">
                  <Stars value={r.rating} />
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[#DCB457] border border-[#DCB457]/30 px-2 py-0.5">
                    {r.event_type}
                  </span>
                </div>
                {r.title && (
                  <h3 className="font-serif-luxe text-xl mt-4">{r.title}</h3>
                )}
                <Quote size={16} strokeWidth={1.3} className="text-[#DCB457]/40 mt-3" />
                <p className="text-sm text-white/75 leading-relaxed mt-2 flex-1">{r.content}</p>
                <div className="mt-5 pt-4 border-t border-[#DCB457]/12 flex flex-wrap items-center justify-between gap-2 text-[11px] uppercase tracking-[0.16em] text-white/45">
                  <span className="text-[#f0d888]">— {r.author_name}</span>
                  <span className="inline-flex items-center gap-3">
                    {r.city && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={11} strokeWidth={1.4} /> {r.city}
                      </span>
                    )}
                    {r.event_date && (
                      <span className="inline-flex items-center gap-1">
                        <Calendar size={11} strokeWidth={1.4} /> {formatDate(r.event_date)}
                      </span>
                    )}
                  </span>
                </div>
                <ShareRow review={r} />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 px-6 md:px-10 lg:px-14 max-w-[900px] mx-auto">
        <div className="card-luxe p-10 md:p-14 text-center">
          <p className="eyebrow">À votre tour</p>
          <h2 className="font-serif-luxe text-4xl md:text-5xl mt-4">
            Rejoignez nos <span className="italic gold-text">clients comblés.</span>
          </h2>
          <p className="text-white/65 text-sm mt-5 max-w-md mx-auto">
            Devis gratuit, sans engagement. Découvrez par vous-même pourquoi nos clients laissent autant d'étoiles.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <Link to="/contact" data-testid="avis-cta-devis" className="btn-gold inline-flex items-center gap-2">
              Demander un devis <ArrowRight size={14} strokeWidth={1.4} />
            </Link>
            <Link to="/espace-client/avis" data-testid="avis-cta-leave" className="btn-ghost-gold inline-flex items-center gap-2">
              Déjà client ? Laisser un avis
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
